import {
  LLMUsageCheckResultStatus,
  type TCheckResult,
} from '@conversed/rest/src/contracts/llm-usages/check-results';
import {
  DEFAULT_REPORT_ISSUE_PARAMS,
  DEFAULT_USE_FOR_AI_KNOWLEDGE_DATA,
  type TConversationsResponse,
} from '@conversed/rest/src/contracts/llm-usages/llm-usages.contract';
import type { TReportIssueParams } from '@conversed/rest/src/contracts/llm-usages/report-issue';
import { updateCheckResultFieldInState } from '@helpers/trainAIHelper';
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useState,
} from 'react';

type TUpdateCheckResultFn = (
  id: string,
  status: LLMUsageCheckResultStatus,
  correctedAnswer: string,
  correctedURL: string,
  reportIssue: boolean,
  reportIssueParams: TReportIssueParams | undefined,
  useForAIKnowledgeData: boolean,
  isReoptimization?: boolean,
  requestedLLMBlockId?: string,
  requestedHotTopicId?: string,
) => Promise<TCheckResult | undefined>;

type TUseBulkReviewParams = {
  conversations: TConversationsResponse;
  setConversations: Dispatch<SetStateAction<TConversationsResponse>>;
  updateCheckResult: TUpdateCheckResultFn;
  requestedHotTopicId: string;
  onComplete: () => void;
};

export const useBulkReview = ({
  conversations,
  setConversations,
  updateCheckResult,
  requestedHotTopicId,
  onComplete,
}: TUseBulkReviewParams) => {
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  // Shared bulk update: apply the same status to every conversation that is still open for a
  // verdict. Two kinds are left alone - see the guards in the loop.
  const runBulkUpdate = useCallback(
    async (status: LLMUsageCheckResultStatus) => {
      setIsBulkProcessing(true);
      try {
        for (
          let conversationIndex = 0;
          conversationIndex < conversations.results.length;
          conversationIndex++
        ) {
          const conversation = conversations.results[conversationIndex];
          // Already carries a verdict of its own.
          if (conversation.checkResult?.status) {
            continue;
          }
          // Already handled from another context: its checkResult is empty (the verdict lives
          // on the sibling document holding the same question), so the check above does not
          // catch it. Approving or flagging one is meaningless - the per-card UI offers only
          // "Re-optimize" for exactly this reason - and writing a status here would take the
          // plain $set path instead of Core's handleReoptimization, leaving two documents for
          // one prompt both looking reviewed.
          if (conversation.isEvaluable === false) {
            continue;
          }
          const checkResult = await updateCheckResult(
            conversation._id,
            status,
            '',
            '',
            false,
            DEFAULT_REPORT_ISSUE_PARAMS,
            DEFAULT_USE_FOR_AI_KNOWLEDGE_DATA,
            false,
            undefined,
            requestedHotTopicId,
          );
          if (checkResult) {
            updateCheckResultFieldInState(
              setConversations,
              conversation._id,
              checkResult,
              false,
            );
          }
        }
        onComplete();
      } finally {
        setIsBulkProcessing(false);
      }
    },
    [
      conversations.results,
      updateCheckResult,
      requestedHotTopicId,
      onComplete,
      setConversations,
    ],
  );

  const handleBulkApproveAll = useCallback(
    () => runBulkUpdate(LLMUsageCheckResultStatus.GOOD),
    [runBulkUpdate],
  );

  const handleBulkMarkAllWrong = useCallback(
    () => runBulkUpdate(LLMUsageCheckResultStatus.IRRELEVANT),
    [runBulkUpdate],
  );

  return {
    isBulkProcessing,
    handleBulkApproveAll,
    handleBulkMarkAllWrong,
  };
};
